import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AccommodationDetails, AttachmentFile, CloseTripDetails, DownloadFileModel, DuplicateTravelPlanData, TransportDetails, TravelAllowancesDetails, TravelDetailsTravelerData, TravelerInfo, TravelPlanAttachment, TravelPlanCreateData, TravelPlanDetails, TravelPlanListItem, TravelPlanSliceModel } from "./travel-plan-interfaces";
import { ErrorResponse, FilteredDropDownModelInit, FilteredDropDownRequest, FilteredDropDownResponse, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { API_BASE_URL as baseUri } from "config/env";

// `tenantId` picks which tenant's reference data to load - needed whenever a plan from
// another tenant is open, since employees / allowances / cost centers are tenant-scoped.
export const GetTravelPlansCreateData = createAsyncThunk<TravelPlanCreateData, string | void, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetTravelPlansCreateData',
    async (tenantId, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/get-create-data' + (tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ''), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TravelPlanCreateData;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// Cross-tenant filter: one repeated `tenantIds` param per tenant. Empty/undefined lets the
// backend fall back to the tenant the user is logged into.
function tenantIdsQuery(tenantIds?: string[]) {
    if (!tenantIds || tenantIds.length === 0) return '';
    return '?' + tenantIds.map(id => `tenantIds=${encodeURIComponent(id)}`).join('&');
}

export const GetAllTravelPlans = createAsyncThunk<TravelPlanListItem[], string[] | void, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetAllTravelPlans',
    async (tenantIds, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/get-all-active' + tenantIdsQuery(tenantIds || undefined), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TravelPlanListItem[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTravelPlan = createAsyncThunk<TravelPlanDetails, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetTravelPlan',
    async (travelPlanId, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/get?travelPlanId=' + travelPlanId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TravelPlanDetails;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetContactsForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetContactsForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateTravelPlan = createAsyncThunk<TravelPlanListItem[], TravelDetailsTravelerData, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/CreateTravelPlan',
    async (createTravelPlanRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(createTravelPlanRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TravelPlanListItem[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTravelerInfoData = createAsyncThunk<TravelerInfo, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetTravelerInfoData',
    async (travelerId, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/get-traveler-info-data?travelerId=' + travelerId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TravelerInfo;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateTravelPlan = createAsyncThunk<TravelDetailsTravelerData, TravelDetailsTravelerData, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/UpdateTravelPlan',
    async (updateTravelPlanRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(updateTravelPlanRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TravelDetailsTravelerData;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeactivateTravelPlan = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/DeactivateTravelPlan',
    async (deactivateTravelPlanId, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/deactivate?travelPlanId=' + deactivateTravelPlanId, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as string;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllInactiveTravelPlans = createAsyncThunk<TravelPlanListItem[], string[] | void, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetAllInactiveTravelPlans',
    async (tenantIds, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/get-all-inactive' + tenantIdsQuery(tenantIds || undefined), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TravelPlanListItem[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const TravelPlanUploadFiles = createAsyncThunk<string, TravelPlanAttachment, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/TravelPlanUploadFiles',
    async (travelPlanAttachment, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/upload-files', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(travelPlanAttachment)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as string;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTravelPlanFiles = createAsyncThunk<AttachmentFile[], string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/GetTravelPlanFiles',
    async (travelPlanId, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/get-files?travelPlanId=' + travelPlanId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AttachmentFile[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const TravelPlanDownloadFiles = createAsyncThunk<AttachmentFile, DownloadFileModel, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/TravelPlanDownloadFiles',
    async (travelPlanAttachment, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/download-file', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(travelPlanAttachment)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AttachmentFile;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// DELETE /api/travel-plans/delete-file
export const DeleteTravelPlanFile = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/DeleteTravelPlanFile',
    async (fileId, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/delete-file?id=' + fileId, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);  

// POST /api/accommodation/create
export const CreateAccommodation = createAsyncThunk<MainResponse, AccommodationDetails, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/CreateAccommodation',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/accommodation/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// PUT /api/accommodation/update
export const UpdateAccommodation = createAsyncThunk<MainResponse, AccommodationDetails, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/UpdateAccommodation',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/accommodation/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// PUT /api/accommodation/delete
export const DeleteAccommodation = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/DeleteAccommodation',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/accommodation/delete?accommodationId=' + request, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// POST /api/transport-management/create
export const CreateTransport = createAsyncThunk<MainResponse, TransportDetails, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/CreateTransport',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/transport-management/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// PUT /api/transport-management/update
export const UpdateTransport = createAsyncThunk<MainResponse, TransportDetails, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/UpdateTransport',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/transport-management/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// PUT /api/transport-management/delete
export const DeleteTransport = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/DeleteTransport',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/transport-management/delete?id=' + request, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateTravelAllowancesManagement = createAsyncThunk<MainResponse, TravelAllowancesDetails, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/CreateTravelAllowancesManagement',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/allowance-management/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateTravelAllowancesManagement = createAsyncThunk<MainResponse, TravelAllowancesDetails, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/UpdateTravelAllowancesManagement',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/allowance-management/update', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CloseTripConfirm = createAsyncThunk<MainResponse, CloseTripDetails, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/CloseTripConfirm',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/close-trips/close', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CloseTripUpdate = createAsyncThunk<MainResponse, CloseTripDetails, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/CloseTripUpdate',
    async (request, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/close-trips/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DuplicateTravelPlan = createAsyncThunk<MainResponse, DuplicateTravelPlanData, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/DuplicateTravelPlan',
    async (data, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/duplicate', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ReactivateTravelPlan = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/TravelPlanSlice/ReactivateTravelPlan',
    async (reactivateTravelPlanId, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/travel-plans/reactivate?travelPlanId=' + reactivateTravelPlanId, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as string;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

const travelPlanInitialValue: TravelPlanSliceModel = {
    travelPlanCreateData: {
        countries: [],
        currencies: [],
        positions: [],
        employees: [],
        collaboratorEmployees: [],
        departments: [],
        tenantLocations: {
            additionalProp1: [],
            additionalProp2: [],
            additionalProp3: [],
        },
        languages: [],
        tenants: [],
        editableTenantIds: [],
        costCenters: [],
    },
    filteredContactsList: FilteredDropDownModelInit,
    statusCode: "",
    message: "",
    showNotification: false,
    reloadData: false,
    isLoading: false,
}


export const TravelPlanSlice = createSlice({
    name: 'TravelPlanSlice',
    initialState: travelPlanInitialValue,
    reducers: {
        resetTravelPlanSlice: (state) => {
            state = travelPlanInitialValue
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
    },
    extraReducers(builder) {
        builder.addCase(GetTravelPlansCreateData.fulfilled, (state, action: PayloadAction<TravelPlanCreateData>) => {
            // state.isLoading = false;
            state.travelPlanCreateData = action.payload
        })
            .addCase(GetTravelPlansCreateData.rejected, (state, action) => {
                // state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTravelPlansCreateData.pending, state => {
                // state.isLoading = true;
            })
            .addCase(GetContactsForDropdown.pending, (state) => {
                state.filteredContactsList = {
                    data: state.filteredContactsList?.data ?? [],
                    loading: true,
                    totalPages: state.filteredContactsList?.totalPages ?? 0,
                    totalItems: state.filteredContactsList?.totalItems ?? 0,
                    currentPage: state.filteredContactsList?.currentPage ?? 1,
                    error: state.filteredContactsList?.error ?? null,
                };
            })
            .addCase(GetContactsForDropdown.fulfilled, (state, action: PayloadAction<FilteredDropDownResponse>) => {
                state.filteredContactsList = {
                    data: action.payload.data ?? [],
                    loading: false,
                    totalPages: action.payload.totalPages ?? 0,
                    totalItems: action.payload.totalItems ?? 0,
                    currentPage: 1,
                    error: null,
                };
            })
            .addCase(GetContactsForDropdown.rejected, (state, action) => {
                state.filteredContactsList = {
                    data: state.filteredContactsList?.data ?? [],
                    loading: false,
                    totalPages: state.filteredContactsList?.totalPages ?? 0,
                    totalItems: state.filteredContactsList?.totalItems ?? 0,
                    currentPage: state.filteredContactsList?.currentPage ?? 1,
                    error: action.payload?.message ?? 'Error in application',
                };
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            // Create/Update/Delete handlers
            .addCase(CreateTravelPlan.fulfilled, (state, action: PayloadAction<TravelPlanListItem[]>) => {
                state.showNotification = true;
                state.message = 'Travel plan created successfully';
                state.statusCode = '200';
            })
            .addCase(CreateTravelPlan.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error creating travel plan';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateTravelPlan.fulfilled, (state, action: PayloadAction<TravelDetailsTravelerData>) => {
                state.showNotification = true;
                state.message = 'Travel plan updated successfully';
                state.statusCode = '200';
            })
            .addCase(UpdateTravelPlan.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error updating travel plan';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeactivateTravelPlan.fulfilled, (state) => {
                state.showNotification = true;
                state.message = 'Travel plan deactivated successfully';
                state.statusCode = '200';
            })
            .addCase(DeactivateTravelPlan.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error deactivating travel plan';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateAccommodation.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.showNotification = true;
                state.message = action.payload.message;
                state.statusCode = '200';
            })
            .addCase(CreateAccommodation.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error creating accommodation';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateAccommodation.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.showNotification = true;
                state.message = action.payload.message;
                state.statusCode = '200';
            })
            .addCase(UpdateAccommodation.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error updating accommodation';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteAccommodation.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.showNotification = true;
                state.message = action.payload.message;
                state.statusCode = '200';
            })
            .addCase(DeleteAccommodation.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error deleting accommodation';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateTransport.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.showNotification = true;
                state.message = action.payload.message;
                state.statusCode = '200';
            })
            .addCase(CreateTransport.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error creating transport';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateTransport.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.showNotification = true;
                state.message = action.payload.message;
                state.statusCode = '200';
            })
            .addCase(UpdateTransport.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error updating transport';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteTransport.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.showNotification = true;
                state.message = action.payload.message;
                state.statusCode = '200';
            })
            .addCase(DeleteTransport.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error deleting transport';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateTravelAllowancesManagement.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.showNotification = true;
                state.message = action.payload.message;
                state.statusCode = '200';
            })
            .addCase(CreateTravelAllowancesManagement.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error creating travel allowances';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateTravelAllowancesManagement.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.showNotification = true;
                state.message = action.payload.message;
                state.statusCode = '200';
            })
            .addCase(UpdateTravelAllowancesManagement.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error updating travel allowances';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteTravelPlanFile.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.showNotification = true;
                state.message = action.payload.message;
                state.statusCode = '200';
            })
            .addCase(DeleteTravelPlanFile.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error deleting file';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CloseTripConfirm.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.showNotification = true;
                state.message = action.payload.message;
                state.statusCode = '200';
            })
            .addCase(CloseTripConfirm.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error closing trip';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CloseTripUpdate.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.showNotification = true;
                state.message = action.payload.message;
                state.statusCode = '200';
            })
            .addCase(CloseTripUpdate.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error updating closed trip';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DuplicateTravelPlan.fulfilled, (state) => {
                state.showNotification = true;
                state.message = 'Travel plan duplicated successfully';
                state.statusCode = '200';
            })
            .addCase(DuplicateTravelPlan.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error duplicating travel plan';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateTravelPlan.fulfilled, (state) => {
                state.showNotification = true;
                state.message = 'Travel plan reactivated successfully';
                state.statusCode = '200';
            })
            .addCase(ReactivateTravelPlan.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error reactivating travel plan';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })

    }}
);

export const { resetTravelPlanSlice, resetStatusCode } = TravelPlanSlice.actions;
export default TravelPlanSlice.reducer;